import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

export const SprayChart = ({
  hits = [],
  width = 700,
  height = 520,
  title = "",
  onPlotHit,
  currentHit = {},
  shouldReset,
}) => {
  const svgRef = useRef(null);
  const [hoverCoords, setHoverCoords] = useState(null);
  const [previewHit, setPreviewHit] = useState(null);

  useEffect(() => {
    if (shouldReset) {
      setPreviewHit(null);
      setHoverCoords(null);
    }
  }, [shouldReset]);

  useEffect(() => {
    if (currentHit?.location) setPreviewHit(currentHit.location);
  }, [currentHit?.location]);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `0 0 ${width} ${height}`);

    const margin = { top: 16, right: 16, bottom: 16, left: 16 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg.append("g").attr("transform", `translate(${margin.left}, ${margin.top})`);

    // Coordinate system for hits (same as your original)
    const xScale = d3.scaleLinear().domain([-260, 260]).range([0, innerWidth]);
    const yScale = d3.scaleLinear().domain([0, 420]).range([innerHeight, 0]);

    const homeX = xScale(0);
    const homeY = yScale(0);

    const feetToPixelsY = yScale(0) - yScale(1);
    const outfieldR = 400 * feetToPixelsY;
    const infieldR = 150 * feetToPixelsY;
    const baseRingOuterR = infieldR * 0.88;
    const baseRingInnerR = infieldR * 0.70;

    const aLeft = (135 * Math.PI) / 180;
    const aRight = (45 * Math.PI) / 180;

    const field = g.append("g").attr("class", "field");
    const markers = g.append("g").attr("class", "markers");

    const polar = (r, a) => ({ x: homeX + r * Math.cos(a), y: homeY - r * Math.sin(a) });

    const sectorPath = (r, strokeClose = true) => {
      const L = polar(r, aLeft);
      const R = polar(r, aRight);
      const largeArc = 0;
      const sweep = 1;
      return [
        `M ${homeX} ${homeY}`,
        `L ${L.x} ${L.y}`,
        `A ${r} ${r} 0 ${largeArc} ${sweep} ${R.x} ${R.y}`,
        strokeClose ? "Z" : `L ${homeX} ${homeY}`,
      ].join(" ");
    };

    const ringSectorPath = (rOuter, rInner) => {
      const Lo = polar(rOuter, aLeft);
      const Ro = polar(rOuter, aRight);
      const Li = polar(rInner, aLeft);
      const Ri = polar(rInner, aRight);
      const largeArc = 0;
      const sweep = 1;
      const sweepBack = 0;

      return [
        `M ${Lo.x} ${Lo.y}`,
        `A ${rOuter} ${rOuter} 0 ${largeArc} ${sweep} ${Ro.x} ${Ro.y}`,
        `L ${Ri.x} ${Ri.y}`,
        `A ${rInner} ${rInner} 0 ${largeArc} ${sweepBack} ${Li.x} ${Li.y}`,
        "Z",
      ].join(" ");
    };

    const C = {
      outfield: "#3f8f3e",
      infieldDirt: "#d3a46c",
      innerGrass: "#3f8f3e",
      outline: "#111827",
      foul: "#ffffff",
      chalk: "rgba(255,255,255,0.92)",
    };

    field
      .append("path")
      .attr("d", sectorPath(outfieldR))
      .attr("fill", C.outfield)
      .attr("stroke", C.outline)
      .attr("stroke-width", 2.5)
      .attr("stroke-linejoin", "round");

    field
      .append("path")
      .attr("d", sectorPath(infieldR))
      .attr("fill", C.infieldDirt)
      .attr("stroke", "none")
      .attr("opacity", 0.98);

    field
      .append("path")
      .attr("d", ringSectorPath(baseRingOuterR, baseRingInnerR))
      .attr("fill", C.infieldDirt)
      .attr("stroke", "none")
      .attr("opacity", 0.98);

    const basePathR = 90 * feetToPixelsY;
    const mound = polar(60.5 * feetToPixelsY, Math.PI / 2);
    const grassSize = basePathR * 0.6;
    const diamondPts = [
      { x: mound.x, y: mound.y - grassSize },
      { x: mound.x + grassSize, y: mound.y },
      { x: mound.x, y: mound.y + grassSize },
      { x: mound.x - grassSize, y: mound.y },
    ];
    field
      .append("path")
      .attr("d", d3.line().x((d) => d.x).y((d) => d.y).curve(d3.curveLinearClosed)(diamondPts))
      .attr("fill", C.innerGrass)
      .attr("stroke", "none");

    const foulLeft = polar(outfieldR, aLeft);
    const foulRight = polar(outfieldR, aRight);

    field
      .append("line")
      .attr("x1", homeX)
      .attr("y1", homeY)
      .attr("x2", foulLeft.x)
      .attr("y2", foulLeft.y)
      .attr("stroke", C.foul)
      .attr("stroke-width", 3)
      .attr("stroke-linecap", "round")
      .attr("opacity", 0.95);

    field
      .append("line")
      .attr("x1", homeX)
      .attr("y1", homeY)
      .attr("x2", foulRight.x)
      .attr("y2", foulRight.y)
      .attr("stroke", C.foul)
      .attr("stroke-width", 3)
      .attr("stroke-linecap", "round")
      .attr("opacity", 0.95);

    const baseLineLen = baseRingOuterR * 0.98;
    const line1B = polar(baseLineLen, aRight);
    const line3B = polar(baseLineLen, aLeft);

    field
      .append("line")
      .attr("x1", homeX)
      .attr("y1", homeY)
      .attr("x2", line1B.x)
      .attr("y2", line1B.y)
      .attr("stroke", C.chalk)
      .attr("stroke-width", 2);

    field
      .append("line")
      .attr("x1", homeX)
      .attr("y1", homeY)
      .attr("x2", line3B.x)
      .attr("y2", line3B.y)
      .attr("stroke", C.chalk)
      .attr("stroke-width", 2);

    field
      .append("circle")
      .attr("cx", mound.x)
      .attr("cy", mound.y)
      .attr("r", 22)
      .attr("fill", C.infieldDirt)
      .attr("stroke", "rgba(0,0,0,0.18)")
      .attr("stroke-width", 1);

    field
      .append("rect")
      .attr("x", mound.x - 8)
      .attr("y", mound.y - 2)
      .attr("width", 16)
      .attr("height", 4)
      .attr("rx", 1)
      .attr("fill", "#ffffff")
      .attr("opacity", 0.85);
    
    const isInsideField = (x, y) => {
      if (y < 0) return false;
      if (Math.abs(x) > y) return false;
      return x * x + y * y <= 400 * 400;
    };

    const drawHit = (hit, isPreview = false) => {
      if (!hit) return;
      if (!isInsideField(hit.x, hit.y)) return;

      markers
        .append("circle")
        .attr("cx", xScale(hit.x))
        .attr("cy", yScale(hit.y))
        .attr("r", isPreview ? 5.5 : 4.5)
        .attr("fill", "#111827")
        .attr("opacity", isPreview ? 0.55 : 0.9);
    };

    hits.forEach((h) => drawHit(h, false));
    if (previewHit) drawHit(previewHit, true);

    const previewGroup = markers.append("g").attr("class", "preview");
    previewGroup
      .append("circle")
      .attr("class", "preview-dot")
      .attr("r", 7)
      .attr("fill", "none")
      .attr("stroke", "#111827")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "4,3")
      .style("display", "none");

    svg.on("mousemove", (event) => {
      const [px, py] = d3.pointer(event, g.node());

      if (px < 0 || px > innerWidth || py < 0 || py > innerHeight) {
        previewGroup.select(".preview-dot").style("display", "none");
        setHoverCoords(null);
        return;
      }

      const hx = xScale.invert(px);
      const hy = yScale.invert(py);

      if (!isInsideField(hx, hy)) {
        previewGroup.select(".preview-dot").style("display", "none");
        setHoverCoords(null);
        return;
      }

      previewGroup.select(".preview-dot").style("display", null).attr("cx", px).attr("cy", py);
      setHoverCoords({ x: hx, y: hy });
    });

    svg.on("mouseleave", () => {
      previewGroup.select(".preview-dot").style("display", "none");
      setHoverCoords(null);
    });

    svg.on("click", (event) => {
      const [px, py] = d3.pointer(event, g.node());
      if (px < 0 || px > innerWidth || py < 0 || py > innerHeight) return;

      const hx = xScale.invert(px);
      const hy = yScale.invert(py);
      if (!isInsideField(hx, hy)) return;

      const newHit = { x: hx, y: hy };
      setPreviewHit(newHit);
      onPlotHit?.(newHit);
    });
  }, [hits, width, height, title, onPlotHit, previewHit]);

  return (
    <div className="relative w-full h-3/4" style={{ zIndex: 0 }}>
      <svg ref={svgRef} className="w-full h-full" style={{ display: "block" }} />
      {hoverCoords && (
        <div className="absolute bottom-4 right-4 bg-white px-3 py-1.5 rounded-full shadow-sm text-sm font-mono text-gray-700">
          ({hoverCoords.x.toFixed(1)}, {hoverCoords.y.toFixed(1)})
        </div>
      )}
    </div>
  );
};
